/**
 * GRAFIK PARCALARI
 *
 * Kural: bir sayi tek basina bir seyi anlatmaz. "%86 dolu" cumlesi ile ayni
 * bilgiyi tasiyan bir halka arasindaki fark, ikincisinin ESIGE NE KADAR YAKIN
 * oldugunu gostermesidir. Bu yuzden her grafik parcasi renk + bicim ile iki
 * kanaldan konusur; renk korlugu olan kullanici icin yalnizca renk yetmez.
 *
 * Teknik tercihler:
 *  - Dis kutuphane YOK. Bir panelde ihtiyac duyulan grafik cesidi bes taneyi
 *    gecmiyor; bunun icin 200 KB'lik bir cizim kutuphanesi tasimak sacma.
 *  - Renkler marka kitindeki veri gorsellestirme dizisinden (--dv-1..6) ve
 *    durum renklerinden gelir; koyu temada kendiliginden degisirler.
 *  - Her sey viewBox ile cizilir, genislik %100'dur: telefonda da bozulmaz.
 */
import { useId, type ReactNode } from "react";

// --------------------------------------------------------------- yardimcilar

/** Orana gore durum rengi — esik mantigini tek yerde tut */
export function oranRengi(oran: number): { renk: string; zemin: string; durum: string } {
  if (oran >= 1) return { renk: "var(--bad)", zemin: "var(--bad-bg)", durum: "doldu" };
  if (oran >= 0.95) return { renk: "var(--bad)", zemin: "var(--bad-bg)", durum: "kritik" };
  if (oran >= 0.8) return { renk: "var(--warn)", zemin: "var(--warn-bg)", durum: "dikkat" };
  return { renk: "var(--ok)", zemin: "var(--ok-bg)", durum: "rahat" };
}

const yuzde = (o: number) => Math.round(Math.max(0, Math.min(1, o)) * 100);

// --------------------------------------------------------------- halka

/**
 * Doluluk halkasi. Kota gibi "sinira ne kadar kaldi" sorularinin cevabi.
 * Esik cizgisi (varsayilan %80) halkanin uzerinde kucuk bir centik olarak
 * durur: kullanici %72'nin esige yakin olup olmadigini bilmek zorunda kalmaz.
 */
export function Halka({
  oran,
  boyut = 92,
  kalinlik = 9,
  ustYazi,
  altYazi,
  esik = 0.8,
  renk,
}: {
  oran: number;
  boyut?: number;
  kalinlik?: number;
  ustYazi?: string;
  altYazi?: string;
  esik?: number;
  renk?: string;
}) {
  const r = 50 - kalinlik / 2 - 1;
  const cevre = 2 * Math.PI * r;
  const dolu = Math.max(0, Math.min(1, oran));
  const d = oranRengi(oran);
  const esikAci = -90 + esik * 360;

  return (
    <svg
      viewBox="0 0 100 100"
      width={boyut}
      height={boyut}
      role="img"
      aria-label={`${ustYazi ?? "Doluluk"}: yüzde ${yuzde(oran)}`}
      style={{ display: "block", flex: "none" }}
    >
      <circle cx="50" cy="50" r={r} fill="none" stroke="var(--n-200)" strokeWidth={kalinlik} />
      <circle
        cx="50"
        cy="50"
        r={r}
        fill="none"
        stroke={renk ?? d.renk}
        strokeWidth={kalinlik}
        strokeLinecap="round"
        strokeDasharray={`${cevre * dolu} ${cevre}`}
        transform="rotate(-90 50 50)"
        style={{ transition: "stroke-dasharray var(--dur, .25s) var(--ease, ease)" }}
      />
      {esik > 0 && esik < 1 && (
        <line
          x1="50"
          y1={50 - r - kalinlik / 2}
          x2="50"
          y2={50 - r + kalinlik / 2}
          stroke="var(--n-0)"
          strokeWidth="2"
          transform={`rotate(${esikAci + 90} 50 50)`}
          opacity="0.85"
        />
      )}
      {ustYazi !== undefined && (
        <text
          x="50"
          y={altYazi ? 47 : 54}
          textAnchor="middle"
          fontSize="21"
          fontWeight="700"
          fill="var(--n-900)"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {ustYazi}
        </text>
      )}
      {altYazi && (
        <text x="50" y="63" textAnchor="middle" fontSize="10" fill="var(--n-500)">
          {altYazi}
        </text>
      )}
    </svg>
  );
}

// --------------------------------------------------------------- doluluk cubugu

/** Yatay doluluk — listede yan yana cok sayida deger gosterirken halkadan iyi */
export function Doluluk({
  oran,
  etiket,
  deger,
  esik = 0.8,
}: {
  oran: number;
  etiket?: ReactNode;
  deger?: ReactNode;
  esik?: number;
}) {
  const d = oranRengi(oran);
  return (
    <div className="doluluk">
      {(etiket || deger) && (
        <div className="doluluk-bas">
          <span className="doluluk-etiket">{etiket}</span>
          <span className="doluluk-deger">{deger}</span>
        </div>
      )}
      <div
        className="doluluk-yol"
        role="img"
        aria-label={`yüzde ${yuzde(oran)} dolu`}
        title={`%${yuzde(oran)} — ${d.durum}`}
      >
        <div className="doluluk-dolu" style={{ width: `${yuzde(oran)}%`, background: d.renk }} />
        {esik > 0 && esik < 1 && (
          <div className="doluluk-esik" style={{ left: `${esik * 100}%` }} aria-hidden />
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------- cubuk grafik

/**
 * Dikey cubuklar — zaman serisi. Deger etiketleri cubuklarin ustunde durur;
 * sifir degerler de bir iz birakir (1px), yoksa "veri yok" ile "deger sifir"
 * ayirt edilemez.
 */
export function Cubuklar({
  veri,
  yukseklik = 120,
  renk = "var(--dv-1)",
  bicim = (n: number) => String(n),
}: {
  veri: Array<{ etiket: string; deger: number; ipucu?: string }>;
  yukseklik?: number;
  renk?: string;
  bicim?: (n: number) => string;
}) {
  if (!veri.length) return null;
  const enB = Math.max(1, ...veri.map((v) => v.deger));
  const n = veri.length;
  const G = 100; // viewBox genisligi
  const bosluk = n > 1 ? 2.2 : 0;
  const w = (G - bosluk * (n - 1)) / n;

  return (
    <div className="cubuklar">
      <svg viewBox={`0 0 ${G} 46`} preserveAspectRatio="none" style={{ height: yukseklik, width: "100%", display: "block" }}>
        {veri.map((v, i) => {
          const h = Math.max(0.6, (v.deger / enB) * 38);
          const x = i * (w + bosluk);
          return (
            <g key={i}>
              <rect x={x} y={46 - h} width={w} height={h} rx={Math.min(1.6, w / 3)} fill={renk}>
                <title>{v.ipucu ?? `${v.etiket}: ${bicim(v.deger)}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div className="cubuk-etiketler">
        {veri.map((v, i) => (
          <span key={i} title={v.ipucu ?? `${v.etiket}: ${bicim(v.deger)}`}>
            <b>{bicim(v.deger)}</b>
            <i>{v.etiket}</i>
          </span>
        ))}
      </div>
    </div>
  );
}

// --------------------------------------------------------------- kivilcim

/** Kucuk egri — bir sayinin yanina "son gunlerde nasil gidiyor" bilgisi koyar */
export function Kivilcim({
  veri,
  genislik = 108,
  yukseklik = 30,
  renk = "var(--brand)",
}: {
  veri: number[];
  genislik?: number;
  yukseklik?: number;
  renk?: string;
}) {
  const id = useId().replace(/:/g, "");
  if (veri.length < 2) return null;

  const enB = Math.max(...veri);
  const enK = Math.min(...veri);
  const aralik = enB - enK || 1;
  const W = 100;
  const H = 30;
  const nokta = veri.map((v, i) => {
    const x = (i / (veri.length - 1)) * W;
    const y = H - 3 - ((v - enK) / aralik) * (H - 6);
    return [x, y] as const;
  });
  const cizgi = nokta.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)}`).join(" ");
  const alan = `${cizgi} L${W} ${H} L0 ${H} Z`;
  const son = nokta[nokta.length - 1]!;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={genislik}
      height={yukseklik}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Son ${veri.length} dönem eğilimi`}
      style={{ display: "block", overflow: "visible" }}
    >
      <defs>
        <linearGradient id={`k${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={renk} stopOpacity="0.28" />
          <stop offset="100%" stopColor={renk} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={alan} fill={`url(#k${id})`} />
      <path d={cizgi} fill="none" stroke={renk} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
            vectorEffect="non-scaling-stroke" />
      <circle cx={son[0]} cy={son[1]} r="2.1" fill={renk} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// --------------------------------------------------------------- durum isigi

/** Yesil/sari/kirmizi nokta — ama yalnizca renkle degil, bicimle de konusur */
export function Isik({
  durum,
  yazi,
}: {
  durum: "iyi" | "uyari" | "kotu" | "bekliyor";
  yazi?: ReactNode;
}) {
  return (
    <span className={"isik isik-" + durum}>
      <i aria-hidden />
      {yazi}
    </span>
  );
}

// --------------------------------------------------------------- dilimler

/** Yatay yiginli cubuk — bir butunun parcalari (ornegin kiraci basina disk) */
export function Dilimler({
  veri,
  bicim = (n: number) => String(n),
}: {
  veri: Array<{ ad: string; deger: number }>;
  bicim?: (n: number) => string;
}) {
  const toplam = veri.reduce((t, v) => t + v.deger, 0);
  if (!toplam) return null;
  const renkler = ["var(--dv-1)", "var(--dv-2)", "var(--dv-3)", "var(--dv-4)", "var(--dv-5)", "var(--dv-6)"];
  return (
    <div className="dilimler">
      <div className="dilim-cubuk" role="img" aria-label="Dağılım">
        {veri.map((v, i) => (
          <span
            key={v.ad}
            style={{ width: `${(v.deger / toplam) * 100}%`, background: renkler[i % renkler.length] }}
            title={`${v.ad}: ${bicim(v.deger)}`}
          />
        ))}
      </div>
      <ul className="dilim-liste">
        {veri.map((v, i) => (
          <li key={v.ad}>
            <i style={{ background: renkler[i % renkler.length] }} aria-hidden />
            <span>{v.ad}</span>
            <b>{bicim(v.deger)}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}
